import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";

function capacityBarClass(pct) {
  if (pct > 85) return "bg-signal-red";
  if (pct > 60) return "bg-signal-amber";
  return "bg-signal-green";
}

export default function Locations() {
  const { user } = useAuth();
  const [locs, setLocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse: "", zone: "", rack: "", shelf: "", bin: "", sku: "", batch: "", quantity: "", capacity: "" });
  const canCreate = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/locations");
    setLocs(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/locations", { ...form, quantity: parseInt(form.quantity) || 0, capacity: form.capacity ? parseInt(form.capacity) : null, batch: form.batch || null });
      toast.success("Location added");
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to add location");
    }
  };

  const tree = {};
  for (const l of locs) {
    tree[l.warehouse] = tree[l.warehouse] || {};
    tree[l.warehouse][l.zone] = tree[l.warehouse][l.zone] || [];
    tree[l.warehouse][l.zone].push(l);
  }

  return (
    <div className="space-y-4" data-testid="locations-page">
      <SectionTitle
        right={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-location-button"><Plus className="w-4 h-4 mr-1" /> Add Location</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Add Bin Location</DialogTitle><DialogDescription className="sr-only">Create a warehouse bin location</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  {["warehouse", "zone", "rack", "shelf", "bin", "sku", "batch"].map((k) => (
                    <div key={k}>
                      <Label className="capitalize">{k}</Label>
                      <Input data-testid={`location-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={k === "bin" ? "A-A03-S2-B04" : ""} />
                    </div>
                  ))}
                  <div>
                    <Label>Quantity</Label>
                    <Input data-testid="location-quantity-input" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                  </div>
                  <div>
                    <Label>Capacity</Label>
                    <Input data-testid="location-capacity-input" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
                  </div>
                </div>
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground mt-2" onClick={create} data-testid="location-create-submit">Add</Button>
              </DialogContent>
            </Dialog>
          )
        }
      >
        Warehouse Locations
      </SectionTitle>

      <div className="space-y-6">
        {Object.entries(tree).map(([wh, zones]) => (
          <div key={wh} className="space-y-3" data-testid={`warehouse-${wh.replace(/\s+/g, "-").toLowerCase()}`}>
            <h3 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" /> {wh}
            </h3>
            {Object.entries(zones).map(([zone, bins]) => (
              <div key={zone} className="ml-2 sm:ml-6 space-y-2">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{zone}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {bins.map((l) => {
                    const pct = l.capacity ? Math.min(100, Math.round((l.quantity / l.capacity) * 100)) : null;
                    return (
                      <div
                        key={l.bin}
                        className={`bg-card border rounded-lg p-4 space-y-2 ${l.mismatch ? "border-signal-red/60" : "border-border"}`}
                        data-testid={`bin-${l.bin}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="metric font-bold text-primary text-sm">{l.bin}</span>
                          {l.mismatch && (
                            <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-signal-red">
                              <AlertTriangle className="w-3.5 h-3.5" /> Mismatch
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">{l.rack} → {l.shelf}</p>
                        <p className="text-sm">
                          <span className="font-semibold">{l.sku}</span>
                          {l.batch && <span className="text-muted-foreground"> · batch {l.batch}</span>}
                        </p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="metric">{l.quantity.toLocaleString()} pcs</span>
                          {l.mismatch && <span className="metric text-signal-red">physical: {(l.physical_quantity ?? 0).toLocaleString()}</span>}
                          {pct != null && <span className="metric text-muted-foreground">{pct}% full</span>}
                        </div>
                        {pct != null && (
                          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className={`h-full rounded-full ${capacityBarClass(pct)}`} style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
